import type { SongMeaning } from '@/types/album';

export const songs: SongMeaning[] = [
  {
    n: 2,
    title: 'Paranoid Android',
    duration: '6:24',
    summary:
      'Vive dentro de una mente saturada que siente asco, persecución y deseo de castigo, pero también hambre de limpieza. No se deja reducir a un solo argumento: es un collage de colapso mental, sátira social y plegaria herida.',
    perspective:
      'La voz cambia de forma todo el tiempo. Suena sitiada, vengativa, burlona y al final casi suplicante. Esa inestabilidad importa más que una historia cerrada.',
    voice: 'Primera persona fragmentada, teatral y poco fiable a propósito.',
    themes: ['Alienación mental', 'Asco hacia la ambición', 'Búsqueda de purga'],
    tone: ['paranoico', 'furioso', 'suplicante'],
    symbols: [
      {
        quote: 'paranoid, but not an android',
        meaning:
          'El "android" concentra el miedo a quedar reducido a un objeto programado, sin dignidad ni interior.',
      },
      {
        quote: 'Gucci little piggy',
        meaning:
          'El cerdo vestido de lujo junta codicia, fealdad moral y clase alta exhibicionista. Ordena toda la burla contra la ambición social.',
      },
      {
        quote: 'Rain down',
        meaning:
          'La lluvia caída desde lo alto trae limpieza, juicio y descarga emocional a la vez. Suena divina o como deseo de que algo arrase con el ruido.',
      },
    ],
    key_lines: [
      {
        lyric: 'I may be paranoid, but not an android',
        explanation:
          'Resume la herida central. Acepta que está alterado, pero se niega a ser tratado como una máquina sin alma.',
      },
      {
        lyric: 'Rain down, rain down',
        explanation:
          'Cambia el aire de la canción. Después del sarcasmo y la violencia verbal, aparece un pedido casi místico de alivio.',
      },
    ],
    alternative: {
      label: 'Plegaria después del colapso',
      description:
        'Después del odio y la humillación, "Rain down" suena como una súplica de limpieza o perdón. Hay fans que oyen la canción menos como estallido mental y más como un viaje desde el ruido hacia una rendición.',
    },
    live: {
      mindset: 'El cuerpo en alerta, como si la canción no dejara respirar.',
      listen_for:
        'El corte brutal entre la rabia cortante del inicio y la subida casi sagrada de "Rain down". Ahí la canción deja de morder y empieza a caer encima.',
      line_to_feel: 'Rain down, rain down',
    },
  },
  {
    n: 6,
    title: 'Karma Police',
    duration: '4:22',
    summary:
      'Vive en una tensión muy Radiohead: el deseo de castigar a los insoportables y la sospecha de que esa furia también te está deformando. Empieza como fantasía de justicia social y acaba como un momento de disolución mental, donde el que denunciaba ya tampoco está a salvo.',
    perspective:
      'Suena teatral y fragmentada. Arranca como alguien que quiere que una fuerza moral venga a poner orden, casi con resentimiento infantil, y termina confesando una pérdida de control que vuelve dudosa toda esa seguridad inicial.',
    voice: 'Primera persona acusatoria que pasa de la burla al desmoronamiento.',
    themes: ['Castigo imaginado', 'Justicia resentida', 'Pérdida de identidad'],
    tone: ['sarcástico', 'desquiciado'],
    symbols: [
      {
        quote: 'Karma police',
        meaning:
          'La "policía del karma" condensa una fantasía de justicia automática. Se siente como el deseo de que algo castigue a la gente cruel o ridícula que uno ya no soporta.',
      },
      {
        quote: 'a detuned radio',
        meaning:
          'La radio desafinada apunta a una humanidad desajustada. Esa persona no solo molesta: emite mal, descompone el ambiente, vuelve la convivencia una interferencia constante.',
      },
    ],
    key_lines: [
      {
        lyric: "This is what you'll get",
        explanation:
          'El golpe colectivo. Suena a amenaza de grupo, a castigo pronunciado por una mayoría ofendida. En vivo la gente lo canta como si pudiera devolver humillaciones con una sentencia simple y fría.',
      },
      {
        lyric: 'For a minute there',
        explanation:
          'Acá se cae la máscara. Después del tono vengativo, aparece el vacío. La canción deja de señalar culpables y muestra a alguien también roto, desbordado, fuera de eje.',
      },
    ],
    alternative: {
      label: 'Colapso mental disfrazado de castigo',
      description:
        'Más que un ajuste de cuentas externo, la canción puede oírse como un brote de paranoia y autodesprecio. La voz pasa de acusar a otros a admitir "I lost myself", y de pronto toda la autoridad del "Karma police" queda temblando.',
    },
    live: {
      mindset: 'Rabia contenida, con un temblor de culpa debajo.',
      listen_for:
        'El cambio desde la acusación casi sarcástica hasta el derrumbe hipnótico del final. Cuando entra "For a minute there", la canción deja de morder y empieza a hundirse.',
      line_to_feel: "This is what you'll get",
    },
  },
  {
    n: 7,
    title: 'Fitter Happier',
    duration: '1:57',
    summary:
      'Clava una idea muy incómoda: la vida "correcta" puede terminar pareciéndose a una jaula pulida. La canción aprieta entre dos fuerzas, el deseo de estar a salvo y el precio de volverse dócil, administrable, casi menos que humano.',
    perspective:
      'Habla desde una voz mecánica, clínica y sin afecto. Suena a computadora, folleto institucional, terapia conductual y propaganda de bienestar todo junto. Esa frialdad es justo lo que la vuelve tan humana por debajo: deja ver cuánto miedo hay detrás de la necesidad de ordenarlo todo.',
    voice: 'Voz impersonal, programada, autoritaria.',
    themes: ['Control social interiorizado', 'Bienestar como deshumanización', 'Miedo al caos'],
    tone: ['clínico', 'opresivo', 'satírico'],
    symbols: [
      {
        quote: 'Like a cat',
        meaning:
          'Los animales muestran sumisión y reducción. El gato atado transmite impotencia elegante, todavía viva. El cerdo enjaulado y medicado ya es cuerpo gestionado.',
      },
      {
        quote: 'baby smiling in back seat',
        meaning:
          'La promesa de seguridad doméstica como propaganda. El bebé sonriendo vende paz, familia y responsabilidad, pero acá suena a imagen publicitaria usada para disciplinar conductas.',
      },
    ],
    key_lines: [
      {
        lyric: 'Fitter, healthier and more productive',
        explanation:
          'Resume la promesa falsa. Suena a mejora personal, pero dicha por una voz deshumanizada queda helada. El ideal de bienestar ya viene contaminado por obediencia y agotamiento.',
      },
      {
        lyric: 'A pig',
        explanation:
          'El cierre. Después de tanta corrección y eficiencia, la persona queda reducida a ganado medicado. La crítica deja de ser abstracta y se vuelve física, social, casi industrial.',
      },
    ],
    alternative: {
      label: 'Autoadoctrinamiento contra el vacío',
      description:
        'La voz no solo enumera mandatos sociales: también suena como una autohipnosis para tapar el pánico. La lista se quiebra con imágenes violentas y absurdas, sobre todo "Like a cat" y "A pig".',
    },
    live: {
      mindset:
        'Una calma incómoda, con la sensación de que te están vendiendo una vida correcta mientras te vacían por dentro.',
      listen_for:
        'El momento en que la lista de hábitos saludables deja de sonar tranquilizadora y empieza a sentirse como vigilancia. Después, cómo las imágenes animales hunden todo en algo más cruel.',
      line_to_feel: 'No chance of escape',
    },
  },
];
